import type { LifecycleStateFilterDto } from '@terra/shared-dto-types';
import { DomainTreeNodeType, type DomainTreeNode } from './domain-tree.utils';

export const DOMAIN_TREE_ROOT_SCOPE = {
  fullyArchived: 'fullyArchived',
  partiallyArchived: 'partiallyArchived',
  fullyInScope: 'fullyInScope',
} as const;

export type DomainTreeRootScope =
  (typeof DOMAIN_TREE_ROOT_SCOPE)[keyof typeof DOMAIN_TREE_ROOT_SCOPE];

export function hasArchivedDescendant(node: DomainTreeNode): boolean {
  if (node.isArchived === true) {
    return true;
  }

  return node.children.some(hasArchivedDescendant);
}

function isFullyArchivedSubtree(node: DomainTreeNode): boolean {
  if (node.children.length === 0) {
    return node.isArchived === true;
  }

  return node.children.every(isFullyArchivedSubtree);
}

export function classifyDomainTreeRoot(
  root: DomainTreeNode,
): DomainTreeRootScope {
  if (root.isArchived === true || isFullyArchivedSubtree(root)) {
    return DOMAIN_TREE_ROOT_SCOPE.fullyArchived;
  }

  if (hasArchivedDescendant(root)) {
    return DOMAIN_TREE_ROOT_SCOPE.partiallyArchived;
  }

  return DOMAIN_TREE_ROOT_SCOPE.fullyInScope;
}

const isAppLevelNode = (node: DomainTreeNode): boolean =>
  node.nodeType === DomainTreeNodeType.AppLevel;

const collectDomainUnits = (
  roots: DomainTreeNode[],
): {
  appLevelRoots: DomainTreeNode[];
  domainRoots: DomainTreeNode[];
} => {
  const appLevelRoots: DomainTreeNode[] = [];
  const domainRoots: DomainTreeNode[] = [];

  for (const root of roots) {
    if (isAppLevelNode(root)) {
      appLevelRoots.push(
        root.children.length > 0 ? { ...root, children: [] } : root,
      );
      domainRoots.push(...root.children);
      continue;
    }

    domainRoots.push(root);
  }

  return { appLevelRoots, domainRoots };
};

export function buildDomainTreeRootScopeMap(
  roots: DomainTreeNode[],
): ReadonlyMap<string, DomainTreeRootScope> {
  const { domainRoots } = collectDomainUnits(roots);
  const rootScopeByRootId = new Map<string, DomainTreeRootScope>();

  for (const root of domainRoots) {
    rootScopeByRootId.set(root.id, classifyDomainTreeRoot(root));
  }

  return rootScopeByRootId;
}

export function getDomainTreeRoot(node: DomainTreeNode): DomainTreeNode {
  let current = node;

  while (current.parent) {
    current = current.parent;
  }

  return current;
}

export function getDomainTreeUnitRoot(node: DomainTreeNode): DomainTreeNode {
  let current = node;

  while (
    current.parent &&
    current.parent.nodeType !== DomainTreeNodeType.AppLevel
  ) {
    current = current.parent;
  }

  return current;
}

export function isGreyedDomainTreeNode(node: DomainTreeNode): boolean {
  let current: DomainTreeNode | undefined = node;

  while (current) {
    if (current.isArchived === true) {
      return true;
    }

    current = current.parent;
  }

  return isFullyArchivedSubtree(node);
}

const resolveListLifecycleState = (
  listLifecycleState?: LifecycleStateFilterDto,
): LifecycleStateFilterDto => listLifecycleState ?? 'active';

export function isDomainTreeRootScopeVisibleForListFilter({
  scope,
  listLifecycleState,
}: {
  scope: DomainTreeRootScope;
  listLifecycleState?: LifecycleStateFilterDto;
}): boolean {
  const effectiveListLifecycleState =
    resolveListLifecycleState(listLifecycleState);

  if (effectiveListLifecycleState === 'active') {
    return scope !== DOMAIN_TREE_ROOT_SCOPE.fullyArchived;
  }

  if (effectiveListLifecycleState === 'archived') {
    return scope !== DOMAIN_TREE_ROOT_SCOPE.fullyInScope;
  }

  return true;
}

export function isDomainTreeNodeVisibleForListFilter({
  node,
  listLifecycleState,
  rootScopeByRootId,
}: {
  node: DomainTreeNode;
  listLifecycleState?: LifecycleStateFilterDto;
  rootScopeByRootId?: ReadonlyMap<string, DomainTreeRootScope>;
}): boolean {
  const unitRoot = getDomainTreeUnitRoot(node);

  if (isAppLevelNode(unitRoot)) {
    return true;
  }

  const scope =
    rootScopeByRootId?.get(unitRoot.id) ?? classifyDomainTreeRoot(unitRoot);

  return isDomainTreeRootScopeVisibleForListFilter({
    scope,
    listLifecycleState,
  });
}

export const isDomainTreeNodeSelectableForListFilter =
  isDomainTreeNodeVisibleForListFilter;

export function splitDomainTreeRootsForListFilter({
  roots,
  listLifecycleState,
}: {
  roots: DomainTreeNode[];
  listLifecycleState?: LifecycleStateFilterDto;
}): {
  leadingRoots: DomainTreeNode[];
  archivedRoots: DomainTreeNode[];
} {
  const { appLevelRoots, domainRoots } = collectDomainUnits(roots);
  const partialRoots: DomainTreeNode[] = [];
  const inScopeRoots: DomainTreeNode[] = [];
  const archivedRoots: DomainTreeNode[] = [];

  for (const root of domainRoots) {
    const scope = classifyDomainTreeRoot(root);

    if (scope === DOMAIN_TREE_ROOT_SCOPE.fullyArchived) {
      archivedRoots.push(root);
      continue;
    }

    if (scope === DOMAIN_TREE_ROOT_SCOPE.partiallyArchived) {
      partialRoots.push(root);
      continue;
    }

    inScopeRoots.push(root);
  }

  const effectiveListLifecycleState =
    resolveListLifecycleState(listLifecycleState);

  if (effectiveListLifecycleState === 'active') {
    return {
      leadingRoots: [...partialRoots, ...appLevelRoots, ...inScopeRoots],
      archivedRoots: [],
    };
  }

  if (effectiveListLifecycleState === 'archived') {
    return {
      leadingRoots: [...partialRoots, ...appLevelRoots],
      archivedRoots,
    };
  }

  return {
    leadingRoots: [...partialRoots, ...appLevelRoots, ...inScopeRoots],
    archivedRoots,
  };
}

export function splitDomainTreeRootsByArchive(roots: DomainTreeNode[]): {
  leadingRoots: DomainTreeNode[];
  archivedRoots: DomainTreeNode[];
} {
  return splitDomainTreeRootsForListFilter({
    roots,
    listLifecycleState: 'all',
  });
}
